import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../services/smartApiService";
import BulkFinancialUploadModal from "./components/BulkFinancialUploadModal";
import "./CompaniesList.scss";

interface Company {
  id: number;
  name: string;
  symbol: string;
  market_cap: number;
  pe_ratio: number;
  revenue_growth: number;
  eps_growth: number;
  rsi: number;
  macd_signal: string;
  order_book_ratio: number;
  beta: number;
  analyst_rating: string;
  one_year_return: number;
  overall_rating: "STRONG_BUY" | "NEUTRAL" | "STRONG_SELL";
  sector_name: string;
  current_price: number;
}

const CompaniesList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeTab, setActiveTab] = useState<"STRONG_BUY" | "NEUTRAL" | "STRONG_SELL">("STRONG_BUY");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Company;
    direction: "asc" | "desc";
  } | null>(null);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = () => {
    setLoading(true);
    get("/api/manage/companies/ratings", { showLoading: false }).subscribe({
      next: (response: any) => {
        setCompanies(response.data.data || []);
        setLoading(false);
      },
      error: (err) => {
        console.error("Failed to load companies:", err);
        setLoading(false);
      },
    });
  };

  const handleSort = (key: keyof Company) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedCompanies = React.useMemo(() => {
    const sorted = [...companies];
    if (sortConfig) {
      sorted.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sorted;
  }, [companies, sortConfig]);

  const handleRowClick = (companyId: number) => {
    navigate(`/company/${companyId}`);
  };

  const formatNumber = (num: number | null | undefined, decimals: number = 2): string => {
    if (num === null || num === undefined) return "N/A";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatMarketCap = (marketCap: number | null | undefined): string => {
    if (marketCap === null || marketCap === undefined) return "N/A";
    if (marketCap >= 1000) return `₹${(marketCap / 1000).toFixed(2)}k Cr`;
    return `₹${marketCap.toFixed(2)} Cr`;
  };

  const getValueColor = (value: number | null | undefined, isBullish: boolean): string => {
    if (value === null || value === undefined) return "";
    return isBullish ? "has-text-success" : "has-text-danger";
  };

  const getPeColor = (pe: number | null | undefined): string => {
    if (pe === null || pe === undefined) return "";
    if (pe < 15) return "has-text-success";
    if (pe > 30) return "has-text-danger";
    return "";
  };

  const getRsiColor = (rsi: number | null | undefined): string => {
    if (rsi === null || rsi === undefined) return "";
    if (rsi < 40) return "has-text-success";
    if (rsi > 70) return "has-text-danger";
    return "";
  };

  const renderSortIcon = (key: keyof Company) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <i className="fa fa-sort ml-2"></i>;
    }
    return sortConfig.direction === "asc" ? (
      <i className="fa fa-sort-up ml-2"></i>
    ) : (
      <i className="fa fa-sort-down ml-2"></i>
    );
  };

  const getFilteredCompanies = () => {
    return sortedCompanies.filter((c) => c.overall_rating === activeTab);
  };

  const getTabCount = (rating: "STRONG_BUY" | "NEUTRAL" | "STRONG_SELL") => {
    return sortedCompanies.filter((c) => c.overall_rating === rating).length;
  };

  const filteredCompanies = getFilteredCompanies();

  return (
    <div className="companies-list">
      <div className="container is-fluid">
        <div className="level mb-5">
          <div className="level-left">
            <div className="level-item">
              <h1 className="title is-3">Active Companies</h1>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <button className="button is-success mr-2" onClick={() => setShowUploadModal(true)}>
                <span className="icon">
                  <i className="fa fa-file-excel"></i>
                </span>
                <span>Bulk Upload Financials</span>
              </button>
            </div>
            <div className="level-item">
              <button className="button is-primary" onClick={loadCompanies}>
                <span className="icon">
                  <i className="fa fa-refresh"></i>
                </span>
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="has-text-centered p-6">
            <div className="loader is-loading"></div>
            <p>Loading companies...</p>
          </div>
        ) : (
          <div className="box">
            <div className="tabs is-boxed">
              <ul>
                <li className={activeTab === "STRONG_BUY" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("STRONG_BUY")}>
                    <span className="icon is-small">
                      <i className="fa fa-arrow-up has-text-success"></i>
                    </span>
                    <span>Buy ({getTabCount("STRONG_BUY")})</span>
                  </a>
                </li>
                <li className={activeTab === "NEUTRAL" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("NEUTRAL")}>
                    <span className="icon is-small">
                      <i className="fa fa-minus has-text-warning"></i>
                    </span>
                    <span>Neutral ({getTabCount("NEUTRAL")})</span>
                  </a>
                </li>
                <li className={activeTab === "STRONG_SELL" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("STRONG_SELL")}>
                    <span className="icon is-small">
                      <i className="fa fa-arrow-down has-text-danger"></i>
                    </span>
                    <span>Sell ({getTabCount("STRONG_SELL")})</span>
                  </a>
                </li>
              </ul>
            </div>

            {filteredCompanies.length === 0 ? (
              <p className="has-text-grey has-text-centered p-5">
                No companies in this category
              </p>
            ) : (
              <div className="table-container">
                <table className="table is-fullwidth is-striped is-hoverable">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                        Company Name {renderSortIcon("name")}
                      </th>
                      <th onClick={() => handleSort("symbol")} style={{ cursor: "pointer" }}>
                        Ticker {renderSortIcon("symbol")}
                      </th>
                      <th onClick={() => handleSort("market_cap")} style={{ cursor: "pointer" }}>
                        Market Cap {renderSortIcon("market_cap")}
                      </th>
                      <th onClick={() => handleSort("pe_ratio")} style={{ cursor: "pointer" }}>
                        P/E Ratio {renderSortIcon("pe_ratio")}
                      </th>
                      <th onClick={() => handleSort("revenue_growth")} style={{ cursor: "pointer" }}>
                        Revenue Growth (YoY) {renderSortIcon("revenue_growth")}
                      </th>
                      <th onClick={() => handleSort("eps_growth")} style={{ cursor: "pointer" }}>
                        EPS Growth {renderSortIcon("eps_growth")}
                      </th>
                      <th onClick={() => handleSort("rsi")} style={{ cursor: "pointer" }}>
                        RSI (14) {renderSortIcon("rsi")}
                      </th>
                      <th onClick={() => handleSort("macd_signal")} style={{ cursor: "pointer" }}>
                        MACD Signal {renderSortIcon("macd_signal")}
                      </th>
                      <th onClick={() => handleSort("order_book_ratio")} style={{ cursor: "pointer" }}>
                        Order Book Ratio {renderSortIcon("order_book_ratio")}
                      </th>
                      <th onClick={() => handleSort("beta")} style={{ cursor: "pointer" }}>
                        Beta {renderSortIcon("beta")}
                      </th>
                      <th onClick={() => handleSort("analyst_rating")} style={{ cursor: "pointer" }}>
                        Analyst Rating {renderSortIcon("analyst_rating")}
                      </th>
                      <th onClick={() => handleSort("one_year_return")} style={{ cursor: "pointer" }}>
                        1Y Return {renderSortIcon("one_year_return")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((company) => (
                      <tr
                        key={company.id}
                        onClick={() => handleRowClick(company.id)}
                        style={{ cursor: "pointer" }}
                        className="company-row"
                      >
                        <td>
                          <strong>{company.name}</strong>
                          <br />
                          <small className="has-text-grey">{company.sector_name || "N/A"}</small>
                        </td>
                        <td>
                          <span className="tag is-info">{company.symbol}</span>
                        </td>
                        <td>{formatMarketCap(company.market_cap)}</td>
                        <td className={getPeColor(company.pe_ratio)}>
                          {formatNumber(company.pe_ratio)}
                        </td>
                        <td className={getValueColor(company.revenue_growth, (company.revenue_growth || 0) > 15)}>
                          {formatNumber(company.revenue_growth)}%
                        </td>
                        <td className={getValueColor(company.eps_growth, (company.eps_growth || 0) > 15)}>
                          {formatNumber(company.eps_growth)}%
                        </td>
                        <td className={getRsiColor(company.rsi)}>
                          {formatNumber(company.rsi)}
                        </td>
                        <td>
                          <span
                            className={`tag ${
                              company.macd_signal === "BULLISH"
                                ? "is-success"
                                : company.macd_signal === "BEARISH"
                                ? "is-danger"
                                : "is-warning"
                            }`}
                          >
                            {company.macd_signal || "N/A"}
                          </span>
                        </td>
                        <td>{formatNumber(company.order_book_ratio)}</td>
                        <td className={getValueColor(company.beta, (company.beta || 0) < 1)}>
                          {formatNumber(company.beta)}
                        </td>
                        <td>
                          <span className="tag is-light">{company.analyst_rating || "N/A"}</span>
                        </td>
                        <td className={getValueColor(company.one_year_return, (company.one_year_return || 0) > 0)}>
                          {formatNumber(company.one_year_return)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <BulkFinancialUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          setShowUploadModal(false);
          loadCompanies();
        }}
      />
    </div>
  );
};

export default CompaniesList;
